import { useCallback, useEffect, useState } from 'react'
import { SAMPLE_IDS, offerUrl } from '../lib/offers'
import { WECHAT_GREEN, WeChatGlyph, WeChatSheet, wechatMessage } from './offer/WeChatSheet'
import { OfferApiError, type OfferReply, fetchOffer, offerOrigin, publishOffer } from './offer/api'
import { useOfferSession } from './offer/session'

// The offer panel under Quote economics. "Create customer link" publishes the offer (sell prices only)
// to this site's Worker (/api/offers, lib/offers-api.ts) and the link opens a real page on any device,
// freightratesmcp.com/offer/<id>, where the client confirms or declines. The status comes back here.
// Publishing needs the seller's session; without one the panel says so and offers the sample pages.

type ExportLine = { name: string; cost: number; sell: number }

const STORE = 'frm-offer-links-v1'
const linkKey = (client: string, route: string) => `${client.trim().toLowerCase()}|${route}`
const readLinks = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(STORE) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}
const rememberLink = (key: string, id: string) => {
  try {
    localStorage.setItem(STORE, JSON.stringify({ ...readLinks(), [key]: id }))
  } catch {
    // A private window keeps the link for this visit only.
  }
}

const SAMPLE_LABEL: Record<string, string> = {
  'q-sample-cnsha-nlrtm': 'Shanghai → Rotterdam',
  'q-sample-cnngb-uslax': 'Ningbo → Los Angeles',
  'q-sample-vnsgn-deham': 'Ho Chi Minh → Hamburg',
}

const usd = (n: number) => `USD ${Math.round(n).toLocaleString('en-US')}`
const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`
}
const clock = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

const safe = (value: string) =>
  value
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/(^-|-$)/g, '')
    .toLowerCase()

export function QuoteDelivery({
  route,
  totalSell,
  totalProfit,
  lines,
  customer,
}: {
  route: string
  totalSell: number
  totalProfit: number
  lines: ExportLine[]
  /** The customer this quote is for, from the customer workspace. */
  customer?: { name: string; email?: string }
}) {
  const session = useOfferSession()
  const signedIn = session.status === 'signed-in'
  const [client, setClient] = useState(customer?.name ?? '')
  const [email, setEmail] = useState(customer?.email ?? '')
  const [offer, setOffer] = useState<OfferReply | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [wechat, setWechat] = useState(false)
  const origin = offerOrigin()
  const url = offer ? offerUrl(origin, offer.doc.id) : ''
  const stale = offer && Math.round(offer.doc.total) !== Math.round(totalSell)

  // A link made earlier for this customer and lane comes back, with its status.
  const savedKey = linkKey(customer?.name ?? '', route)
  useEffect(() => {
    const id = readLinks()[savedKey]
    if (!id) return
    fetchOffer(id, true)
      .then(setOffer)
      .catch(() => setOffer(null))
  }, [savedKey])

  // The seller's view of the link stays current while the panel is open.
  const offerId = offer?.doc.id
  const decided = Boolean(offer?.decision)
  useEffect(() => {
    if (!offerId || decided) return
    const t = window.setInterval(() => {
      fetchOffer(offerId, true)
        .then(setOffer)
        .catch(() => {})
    }, 8000)
    return () => window.clearInterval(t)
  }, [offerId, decided])

  const publish = useCallback(async (): Promise<OfferReply | null> => {
    if (offer && !stale) return offer
    if (session.status !== 'signed-in') {
      setError('Sign in to send an offer.')
      return null
    }
    if (!client.trim()) {
      setError('Add the client’s company name first.')
      return null
    }
    const customerLines = lines
      .filter((l) => l.sell > 0 && l.name.trim())
      .map((l) => ({ name: l.name.trim().slice(0, 120), amount: Math.round(l.sell * 100) / 100 }))
    if (!customerLines.length) {
      setError('Add at least one charge with a sell price.')
      return null
    }
    setBusy(true)
    setError('')
    try {
      const { seller } = session
      const made = await publishOffer(
        {
          validUntil: new Date(Date.now() + 14 * 864e5).toISOString(),
          member: seller.member,
          network: 'FreightRatesMCP',
          seller: { name: seller.name, ...(seller.email ? { email: seller.email } : {}) },
          client: client.trim().slice(0, 120),
          route,
          currency: 'USD',
          lines: customerLines,
        },
        await session.token()
      )
      rememberLink(linkKey(client, route), made.doc.id)
      if (client.trim() !== (customer?.name ?? '').trim()) rememberLink(savedKey, made.doc.id)
      setOffer(made)
      return made
    } catch (e) {
      setError(
        e instanceof OfferApiError && e.status === 401
          ? 'Your sign-in has expired. Sign in again to send the offer.'
          : e instanceof Error && e.message === 'Failed to fetch'
            ? 'Could not reach FreightRatesMCP. Check the connection and try again.'
            : `Could not publish: ${e instanceof Error ? e.message : 'unknown error'}`
      )
      return null
    } finally {
      setBusy(false)
    }
  }, [offer, stale, session, client, customer?.name, savedKey, route, lines])

  const message = (link: string, ref: string) =>
    `Freight offer ${ref} for ${route}: ${usd(totalSell)}. Review and confirm: ${link}`

  const copyLink = async () => {
    const o = await publish()
    if (!o) return
    await navigator.clipboard?.writeText(offerUrl(origin, o.doc.id)).catch(() => {})
    setCopied(true)
  }
  const emailIt = async () => {
    const o = await publish()
    if (!o) return
    const link = offerUrl(origin, o.doc.id)
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(`Freight offer ${o.doc.ref}`)}&body=${encodeURIComponent(message(link, o.doc.ref))}`
  }
  const whatsapp = async () => {
    // Open the tab now, while the click still counts, then point it at WhatsApp once the link exists.
    const tab = window.open('', '_blank')
    const o = await publish()
    if (!o) {
      tab?.close()
      return
    }
    const text = encodeURIComponent(message(offerUrl(origin, o.doc.id), o.doc.ref))
    if (tab) tab.location.href = `https://wa.me/?text=${text}`
  }
  const openWechat = async () => {
    if (await publish()) setWechat(true)
  }

  const download = (format: 'csv' | 'json' | 'xls') => {
    const quoteRef = offer?.doc.ref ?? `Q-${safe(route).slice(0, 14).toUpperCase()}`
    const rows = [
      ['Charge', 'Net cost', 'Customer sell', 'Profit'],
      ...lines.map((line) => [line.name, line.cost, line.sell, line.sell - line.cost]),
    ]
    const body =
      format === 'json'
        ? JSON.stringify({ quoteRef, route, totalSell, totalProfit, lines }, null, 2)
        : rows.map((row) => row.join(format === 'xls' ? '\t' : ',')).join('\n')
    const blob = new Blob([body], {
      type: format === 'json' ? 'application/json' : 'text/plain;charset=utf-8',
    })
    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(blob)
    anchor.download = `${quoteRef}.${format}`
    anchor.click()
    URL.revokeObjectURL(anchor.href)
  }

  const statusLine = !offer ? null : offer.decision ? (
    <span className={`oqd-state ${offer.decision.kind}`}>
      {offer.decision.kind === 'confirmed' ? '✓ Confirmed' : 'Declined'} by {offer.decision.name}
      {offer.decision.company ? `, ${offer.decision.company}` : ''}
      {offer.decision.via === 'wechat' ? ' via WeChat' : ' by email'} · {clock(offer.decision.at)}
      {offer.decision.po ? ` · ${offer.decision.po}` : ''}
      {offer.decision.reason ? ` · “${offer.decision.reason}”` : ''}
    </span>
  ) : offer.status === 'expired' ? (
    <span className="oqd-state expired">Expired. Publish a new version to send it again.</span>
  ) : offer.views.length ? (
    <span className="oqd-state viewed">
      Opened {offer.views.length} time{offer.views.length === 1 ? '' : 's'}, last{' '}
      {ago(offer.views[offer.views.length - 1] ?? offer.doc.createdAt)}. Waiting for their answer.
    </span>
  ) : (
    <span className="oqd-state live">Live. Not opened yet.</span>
  )

  const badge = !offer
    ? 'Private draft'
    : offer.decision
      ? offer.decision.kind === 'confirmed'
        ? 'Confirmed'
        : 'Declined'
      : offer.status === 'expired'
        ? 'Expired'
        : 'Offer live'

  const canSend = signedIn || Boolean(offer && !stale)

  return (
    <section className="qdl">
      <header>
        <div>
          <span>Share and confirmation</span>
          <h3>Send the offer. Let the customer confirm.</h3>
        </div>
        <b className={offer ? (offer.decision?.kind === 'declined' ? 'no' : 'live') : ''}>
          {badge}
        </b>
      </header>
      <div className="qdl-grid">
        <div className="qdl-link">
          <span>Secure customer link</span>
          {offer ? (
            <div>
              <code>{url}</code>
              <button type="button" onClick={copyLink}>
                {copied ? 'Copied ✓' : 'Copy link'}
              </button>
            </div>
          ) : session.status === 'signed-out' ? (
            <p className="oqd-signin">
              Sign in to send an offer: your client gets a page to review it and confirm.{' '}
              <a href={`${origin}/account`} target="_blank" rel="noreferrer">
                Sign in ↗
              </a>
            </p>
          ) : (
            <div className="oqd-make">
              <label>
                <small>Client</small>
                <input
                  aria-label="Client company"
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  placeholder="Client company"
                />
              </label>
              <button type="button" onClick={() => void publish()} disabled={busy || !signedIn}>
                {busy ? 'Publishing…' : 'Create customer link'}
              </button>
            </div>
          )}
          {statusLine ? (
            <p className="oqd-status">
              {statusLine}
              {stale && !offer?.decision && signedIn && (
                <button type="button" onClick={() => void publish()} disabled={busy}>
                  Prices changed. Publish version 2
                </button>
              )}
            </p>
          ) : (
            <small>
              {session.status === 'signed-in'
                ? `Sent as ${session.seller.name}, ${session.seller.member}. `
                : ''}
              The client sees sell prices only, never your costs. Opening the link does not accept
              the offer: they confirm it themselves, by WeChat or email.
            </small>
          )}
          {error && <small className="oqd-err">{error}</small>}
        </div>
        <div className="qdl-confirm">
          <span>Customer confirmation portal</span>
          <div className="qdl-flow">
            <b className={offer ? 'on' : ''}>Review</b>
            <i />
            <b className={offer?.decision ? 'on' : ''}>Confirm</b>
            <i />
            <b className={offer?.decision ? 'on' : ''}>Audit record</b>
          </div>
          {offer ? (
            <a href={url} target="_blank" rel="noreferrer">
              Open the customer page ↗
            </a>
          ) : (
            <p className="oqd-samples">
              Try it as a client:{' '}
              {SAMPLE_IDS.map((id, i) => (
                <span key={id}>
                  {i > 0 && ' · '}
                  <a href={offerUrl(origin, id)} target="_blank" rel="noreferrer">
                    {SAMPLE_LABEL[id]}
                  </a>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
      <div className="qdl-send">
        <label>
          <span>Invite customer</span>
          <input
            aria-label="Customer offer email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="button" className="primary" onClick={emailIt} disabled={busy || !canSend}>
          Email the offer
        </button>
        <button type="button" onClick={whatsapp} disabled={busy || !canSend}>
          WhatsApp
        </button>
        <button
          type="button"
          className="oqd-wechat"
          onClick={openWechat}
          disabled={busy || !canSend}
        >
          <WeChatGlyph size={14} /> WeChat
        </button>
      </div>
      <footer>
        <span>Save or attach</span>
        <button type="button" onClick={() => window.print()}>
          PDF
        </button>
        <button type="button" onClick={() => download('xls')}>
          Excel
        </button>
        <button type="button" onClick={() => download('csv')}>
          CSV
        </button>
        <button type="button" onClick={() => download('json')}>
          JSON
        </button>
        <small>
          {offer
            ? `${offer.doc.ref} · made ${clock(offer.doc.createdAt)} · valid to ${clock(offer.doc.validUntil)}`
            : 'The link records views and the final confirmation.'}
        </small>
      </footer>
      {wechat && offer && (
        <WeChatSheet
          url={url}
          client={offer.doc.client}
          message={wechatMessage({
            client: offer.doc.client,
            ref: offer.doc.ref,
            route,
            total: usd(offer.doc.total),
            url,
          })}
          onClose={() => setWechat(false)}
        />
      )}
      <style>{styles}</style>
    </section>
  )
}

const styles = `
.qdl{border-top:1px solid var(--border);background:color-mix(in srgb,var(--surface-1) 55%,var(--surface-0))}.qdl *{box-sizing:border-box}.qdl button,.qdl input,.qdl a{font:inherit}.qdl>header{display:flex;justify-content:space-between;align-items:center;padding:14px 16px}.qdl>header span,.qdl-link>span,.qdl-confirm>span,.qdl-send label>span,.qdl>footer>span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.qdl>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:17px;margin:3px 0 0}.qdl>header>b{font-size:8px;padding:4px 7px;border-radius:10px;background:var(--surface-1);color:var(--text-muted)}.qdl>header>b.live{background:#10b98118;color:#047857}.qdl>header>b.no{background:#ef444418;color:#b91c1c}.qdl-grid{display:grid;grid-template-columns:1.35fr .85fr;border-top:1px solid var(--border);border-bottom:1px solid var(--border)}.qdl-grid>div{padding:13px 16px}.qdl-grid>div:first-child{border-right:1px solid var(--border)}.qdl-link>div{display:flex;margin-top:7px}.qdl-link code{min-width:0;flex:1;border:1px solid var(--border);background:var(--surface-0);border-radius:8px 0 0 8px;padding:8px 9px;font-family:var(--mono,ui-monospace,monospace);font-size:8.5px;overflow:hidden;text-overflow:ellipsis}.qdl-link>div>button{border:0;background:var(--text-primary);color:var(--surface-0);border-radius:0 8px 8px 0;padding:0 11px;font-size:8.5px;font-weight:700}.qdl-link small{display:block;color:var(--text-muted);font-size:7.5px;margin-top:5px}.qdl-flow{display:flex;align-items:center;gap:6px;margin:10px 0}.qdl-flow b{font-size:8px;color:var(--text-muted)}.qdl-flow b.on{color:#047857}.qdl-flow i{height:1px;flex:1;background:var(--border)}.qdl-confirm a{color:var(--frd-accent);font-size:8.5px;font-weight:700;text-decoration:none}.qdl-send{display:flex;align-items:end;gap:7px;padding:12px 16px}.qdl-send label{display:grid;gap:4px;flex:1}.qdl-send input{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px 9px;font-size:9px}.qdl-send button,.qdl-send a{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px 10px;font-size:8.5px;font-weight:700;text-decoration:none;cursor:pointer}.qdl-send .primary{background:var(--text-primary);color:var(--surface-0);border-color:var(--text-primary)}.qdl>footer{display:flex;align-items:center;gap:6px;padding:10px 16px;border-top:1px solid var(--border)}.qdl>footer button{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:7px;padding:6px 9px;font-size:8px;font-weight:700}.qdl>footer small{margin-left:auto;color:var(--text-muted);font-size:7.5px}
.qdl-send .oqd-wechat{display:inline-flex;align-items:center;gap:5px;background:${WECHAT_GREEN};border-color:${WECHAT_GREEN};color:#fff}
.qdl button:disabled{opacity:.6;cursor:default}
.oqd-make{display:flex;gap:7px;align-items:end;margin-top:7px}.oqd-make label{flex:1;display:grid;gap:3px}.oqd-make small{font-size:7.5px;color:var(--text-muted);margin:0}
.oqd-make input{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:8px 9px;font-size:9px}
.qdl-link .oqd-make button{border:0;background:var(--frd-accent);color:#fff;border-radius:8px;padding:8px 12px;font-size:8.5px;font-weight:700;cursor:pointer;white-space:nowrap}
.oqd-signin{margin:7px 0 0;font-size:8.5px;line-height:1.6;color:var(--text-muted)}.oqd-signin a{color:var(--frd-accent);font-weight:700;text-decoration:none}
.oqd-status{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:7px 0 0;font-size:8.5px}
.oqd-status button{border:1px solid var(--border);background:var(--surface-0);border-radius:7px;padding:4px 8px;font-size:8px;font-weight:700;cursor:pointer}
.oqd-state{font-weight:600}.oqd-state.live{color:var(--text-muted)}.oqd-state.viewed{color:#b45309}.oqd-state.confirmed{color:#047857}.oqd-state.declined,.oqd-state.expired{color:#b91c1c}
.oqd-err{color:#b91c1c!important}
.oqd-samples{margin:0;font-size:8.5px;color:var(--text-muted);line-height:1.7}
@media(max-width:760px){.qdl-grid{grid-template-columns:1fr}.qdl-grid>div:first-child{border-right:0;border-bottom:1px solid var(--border)}.qdl-send{align-items:stretch;display:grid;grid-template-columns:1fr 1fr}.qdl-send label{grid-column:1/-1}.qdl-send .primary{grid-column:1/-1}.qdl-send a,.qdl-send>button:not(.primary){text-align:center;justify-content:center}.qdl>footer{flex-wrap:wrap}.qdl>footer>span{width:100%}.qdl>footer small{width:100%;margin:3px 0 0}.oqd-make{flex-direction:column;align-items:stretch}}
`
